/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createCompositeEntityStore } from 'src/luis/core/stores/compositeEntityStore';
import { createConversationFeatureStore } from 'src/luis/core/stores/conversationFeatureStore';
import { createEntityRoleStore } from 'src/luis/core/stores/entityRoleStore';
import { createEntityStore } from 'src/luis/core/stores/entityStore';
import { createHierarchicalEntityStore } from 'src/luis/core/stores/hierarchicalEntityStore';
import { createListEntityStore } from 'src/luis/core/stores/listEntityStore';
import { createPrebuiltEntityStore } from 'src/luis/core/stores/prebuiltEntityStore';
import { createRegexEntityStore } from 'src/luis/core/stores/regexEntityStore';
import { createSimpleEntityStore } from 'src/luis/core/stores/simpleEntityStore';
import { createSublistStore } from 'src/luis/core/stores/sublistStore';
import { FeatureSwagger } from 'src/luis/modules/features/types/FeatureSwagger';
import { isPhraseList } from 'src/luis/modules/features/utils/conversationFeatureUtils';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { Sublist } from 'src/luis/projects/entities/types/Sublist';
import { CompositeEntitySwagger } from 'src/luis/projects/entities/types/swagger/CompositeEntitySwagger';
import { EntityRoleSwagger } from 'src/luis/projects/entities/types/swagger/EntityRoleSwagger';
import { EntitySwagger } from 'src/luis/projects/entities/types/swagger/EntitySwagger';
import { HierarchicalEntitySwagger } from 'src/luis/projects/entities/types/swagger/HierarchicalEntitySwagger';
import { ListEntitySwagger, SubListSwagger } from 'src/luis/projects/entities/types/swagger/ListEntitySwagger';
import { PatternAnyEntitySwagger } from 'src/luis/projects/entities/types/swagger/PatternAnyEntitySwagger';
import { PrebuiltEntitySwagger } from 'src/luis/projects/entities/types/swagger/PrebuiltEntitySwagger';
import { RegexEntitySwagger } from 'src/luis/projects/entities/types/swagger/RegexEntitySwagger';
import { SimpleEntitySwagger } from 'src/luis/projects/entities/types/swagger/SimpleEntitySwagger';
import { ModelType } from 'src/luis/types/ModelType';
import { CompositeEntityStore } from 'src/luis/types/stores/entities/CompositeEntityStore';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { HierarchicalEntityStore } from 'src/luis/types/stores/entities/HierarchicalEntityStore';
import { ListEntityStore } from 'src/luis/types/stores/entities/ListEntityStore';
import { PatternAnyEntityStore } from 'src/luis/types/stores/entities/PatternAnyEntityStore';
import { PrebuiltEntityStore } from 'src/luis/types/stores/entities/PrebuiltEntityMetadataStore';
import { RegexEntityStore } from 'src/luis/types/stores/entities/RegexEntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { FeatureStore } from 'src/luis/types/stores/features/FeatureStore';

export const prebuiltEntitySwaggerToEntityStore = (entitySwagger: EntitySwagger, labelsCount?: number): EntityStore => {
    const { id, name, typeId, roles } = entitySwagger;
    const entityStore = createEntityStore(id, {
        name,
        typeId,
        labelsCount
    });

    entityRolesSwaggerToEntityRolesStore(entityStore, roles);

    return entityStore;
};

export const simpleEntitySwaggerToSimpleEntityStore = (
    simpleEntitySwagger: SimpleEntitySwagger,
    labelsCount?: number
): SimpleEntityStore => {
    const children = simpleEntitySwagger.children.map(simpleEntityChildSwaggerToSimpleEntityChildStore);

    const entityStore = createSimpleEntityStore(simpleEntitySwagger.id, {
        name: simpleEntitySwagger.name,
        typeId: simpleEntitySwagger.typeId,
        labelsCount,
        children
    });

    entityStore.featureStoreHouse.setItems(featureSwaggerToFeature(simpleEntitySwagger.features));

    entityRolesSwaggerToEntityRolesStore(entityStore, simpleEntitySwagger.roles);

    return entityStore;
};

export const simpleEntityChildSwaggerToSimpleEntityChildStore = (simpleEntityChildSwagger: SimpleEntitySwagger): SimpleEntityStore => {
    const children = simpleEntityChildSwagger.children.map(simpleEntityChildSwaggerToSimpleEntityChildStore);

    const store = simpleEntityChildSwagger.instanceOf
        ? createSimpleEntityStore(simpleEntityChildSwagger.id, <unknown>simpleEntityChildSwagger)
        : createSimpleEntityStore(simpleEntityChildSwagger.id, {
              name: simpleEntityChildSwagger.name,
              typeId: simpleEntityChildSwagger.typeId,
              children
          });

    store.featureStoreHouse.setItems(featureSwaggerToFeature(simpleEntityChildSwagger.features));

    return store;
};

export const simpleEntityToSimpleEntitySwagger = (entity: SimpleEntity): SimpleEntitySwagger => {
    const mappedChildren = entity.children.map(simpleEntityToSimpleEntitySwagger);

    return entity.instanceOf
        ? {
              id: entity.id.toString(),
              children: mappedChildren,
              features: [],
              instanceOf: entity.instanceOf.name,
              name: entity.name,
              readableType: '',
              roles: [],
              typeId: entity.typeId
          }
        : {
              id: entity.id.toString(),
              children: mappedChildren,
              features: [],
              instanceOf: null,
              name: entity.name,
              readableType: '',
              roles: [],
              typeId: entity.typeId
          };
};

/**
 * Mapper to convert a SimpleEntityStore to a SimpleEntitySwagger.
 * This mapper doesn't map features nor roles. It is used to create new simple entities.
 * @param entityStore The entityStore to map.
 */
export const simpleEntityStoreToSimpleEntitySwagger = (entityStore: SimpleEntityStore): SimpleEntitySwagger => {
    const mappedChildren = entityStore.children.map(simpleEntityStoreToSimpleEntitySwagger);

    return {
        id: entityStore.id.toString(),
        children: mappedChildren,
        features: [],
        instanceOf: <string>entityStore.instanceOf,
        name: entityStore.name,
        readableType: '',
        roles: [],
        typeId: entityStore.typeId
    };
};

export const regexEntitySwaggerToRegexEntityStore = (regexEntitySwagger: RegexEntitySwagger, labelsCount?: number): RegexEntityStore => {
    const { id, name, regexPattern, roles, typeId } = regexEntitySwagger;
    const entityStore = createRegexEntityStore(id, {
        name,
        typeId,
        pattern: regexPattern,
        labelsCount
    });

    entityRolesSwaggerToEntityRolesStore(entityStore, roles);

    return entityStore;
};

export const entityRoleSwaggerToEntityRoleStore = (entityRoleSwagger: EntityRoleSwagger) =>
    createEntityRoleStore(entityRoleSwagger.id, { name: entityRoleSwagger.name });

export const patternAnyEntityToPatternAnyStore = (
    patternAnyEntitySwagger: PatternAnyEntitySwagger,
    labelsCount?: number
): PatternAnyEntityStore => <PatternAnyEntityStore>prebuiltEntitySwaggerToEntityStore(patternAnyEntitySwagger, labelsCount);

export const listEntitySwaggerToListEntityStore = (listEntitySwagger: ListEntitySwagger, labelsCount?: number): ListEntityStore => {
    const { id, name, typeId, roles } = listEntitySwagger;
    const entityStore = createListEntityStore(id, {
        name,
        typeId,
        sublists: listEntitySwagger.subLists.map(sl =>
            createSublistStore(sl.id, { id: sl.id, name: sl.canonicalForm, values: sl.list.slice() })
        ),
        labelsCount
    });

    entityRolesSwaggerToEntityRolesStore(entityStore, roles);

    return entityStore;
};

export const compositeEntitySwaggerToCompositeEntityStore = (
    compositeEntitySwagger: CompositeEntitySwagger,
    labelsCount?: number
): CompositeEntityStore => {
    const { id, name, children, roles, typeId } = compositeEntitySwagger;
    const entityStore = createCompositeEntityStore(id, {
        name,
        typeId,
        children: children.map(c => c.id),
        labelsCount
    });

    entityStore.featureStoreHouse.setItems(featureSwaggerToFeature(compositeEntitySwagger.features));

    entityRolesSwaggerToEntityRolesStore(entityStore, roles);

    return entityStore;
};

export const HierarchicalEntitySwaggerToHierarchicalEntityStore = (
    compositeEntitySwagger: HierarchicalEntitySwagger,
    labelsCount?: number
): HierarchicalEntityStore => {
    const { id, name, children, typeId } = compositeEntitySwagger;

    return createHierarchicalEntityStore(id, {
        name,
        typeId,
        children: children.map(c => ({ id: c.id, name: c.name, typeId: ModelType.HierarchicalChild, roles: [] })),
        labelsCount
    });
};

export const entitySwaggerToEntityStore = (entitySwagger: EntitySwagger, labelsCount?: number): EntityStore => {
    switch (entitySwagger.typeId) {
        case ModelType.List:
            return listEntitySwaggerToListEntityStore(<ListEntitySwagger>entitySwagger, labelsCount);
        case ModelType.Composite:
            return <CompositeEntityStore>compositeEntitySwaggerToCompositeEntityStore(<CompositeEntitySwagger>entitySwagger, labelsCount);
        case ModelType.Hierarchical:
            return HierarchicalEntitySwaggerToHierarchicalEntityStore(<HierarchicalEntitySwagger>entitySwagger, labelsCount);
        case ModelType.PatternAny:
            return patternAnyEntityToPatternAnyStore(<PatternAnyEntitySwagger>entitySwagger, labelsCount);
        case ModelType.Regex:
            return regexEntitySwaggerToRegexEntityStore(<RegexEntitySwagger>entitySwagger, labelsCount);
        case ModelType.Simple:
            return <SimpleEntityStore>simpleEntitySwaggerToSimpleEntityStore(<SimpleEntitySwagger>entitySwagger, labelsCount);
        case ModelType.Prebuilt:
            return prebuiltEntitySwaggerToEntityStore(entitySwagger, labelsCount);
        default:
    }
};

export const prebuiltEntitySwaggerToPrebuiltEntityStore = (prebuiltEntitySwagger: PrebuiltEntitySwagger): PrebuiltEntityStore =>
    createPrebuiltEntityStore(prebuiltEntitySwagger);

export const sublistSwaggerToSublistStore = (sublistSwagger: SubListSwagger): Sublist => ({
    id: sublistSwagger.id,
    name: sublistSwagger.canonicalForm,
    values: sublistSwagger.list
});

const entityRolesSwaggerToEntityRolesStore = (entityStore: EntityStore, roles: readonly EntityRoleSwagger[]) =>
    entityStore.entityRoleStoreHouse.setItems(roles ? roles.map(r => createEntityRoleStore(r.id, { name: r.name })) : []);

const featureSwaggerToFeature = (featureSwagger: FeatureSwagger[]): FeatureStore[] => {
    if (!featureSwagger) {
        return [];
    }

    return featureSwagger.map(f => {
        const id = isPhraseList(f) ? f.featureId : f.modelId;
        const typeId = isPhraseList(f) ? 'feature' : 'model';
        const name = isPhraseList(f) ? f.featureName : f.modelName;
        const { isRequired } = f;

        return createConversationFeatureStore(id, { typeId, name, isRequired });
    });
};
